import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatBellLine } from '../lib/formatBellLine';
import { resolveReminderDate } from '../lib/resolveReminder';
import {
  normalizeRecurrence,
  parseReminderLine,
  formatTimeShort,
  formatMonthDay,
  computeNextOccurrence,
} from '../lib/reminderParser';

/**
 * ReminderEditPopover — NL-first reminder editor.
 *
 * Walter 2026-05-12 redesign: the previous structured form (separate
 * text / date / time / recurrence-dropdown / NL field / quick-action
 * buttons) layered too much UI on what's really a single piece of NL
 * text. The new popover is a single editable input showing the
 * canonical NL form ("every Tue at 8am: reserve listicity") plus a
 * non-editable "next instance" annotation for recurring reminders.
 * Notes textarea stays, source preview on the right stays.
 *
 * The user types NL freely; on save we re-parse via parseReminderLine
 * (the same parser the capture flow uses), extract recurrence /
 * parsedDate / text, and persist the patch. If the parse fails the
 * existing values are preserved — we don't trash a working reminder
 * because the user mid-edited the rule.
 *
 * Saves on click-outside and on the explicit save button. Esc
 * dismisses without saving the latest unsaved keystrokes.
 *
 * Props (mostly unchanged from the structured version):
 *   reminder       — the reminder being edited
 *   sourceList     — list document the reminder came from, or null
 *   notes          — array of continuation-line strings
 *   anchorRect     — DOMRect of the agenda row that triggered open
 *   onUpdate       — (id, patch) => void
 *   onUpdateNotes  — (reminder, newNotesText) => void
 *   onJump         — () => void; "open source list" link
 *   onClose        — () => void
 */
export default function ReminderEditPopover({
  reminder,
  sourceList,
  notes = [],
  anchorRect,
  onUpdate,
  onUpdateNotes,
  onJump,
  onClose,
  // Walter 2026-05-13 (s1): full row-action set inside the popover.
  // Mirror the agenda-row affordances so the popover is a complete
  // surface — you can mark done, snooze, drop the date, deactivate,
  // or delete without bouncing back to the agenda row first.
  onMarkDone,
  onSnooze,
  onRemoveDate,
  onDeactivate,
  onDelete,
  // Walter 2026-05-13 (round 9): explicit "kill the recurring rule"
  // action, only meaningful on reminders that have a recurrence.
  // Wired by AgendaPanel for recurring docs; left null for one-offs
  // (the action row hides it when not provided).
  onStopReminding,
  // Walter 2026-05-12: "create" mode — when reminder.id is missing
  // and onCreate is provided, the popover renders blank, treats the
  // save action as a create-new (not patch-existing), and skips the
  // row-action set (done/snooze/etc. don't apply to a draft). On
  // save, onCreate receives the parsed payload + notes text.
  onCreate,
  // Walter 2026-09-14: his default reminder time ("HH:MM"), so a date
  // typed here without a time resolves exactly as it would in a list.
  defaultReminderTime,
}) {
  const isCreateMode = !reminder?.id && typeof onCreate === 'function';
  // Walter 2026-05-14: explicit-confirm flow inside the popover too.
  // Tapping "did it" on a recurring reminder shows an inline confirm
  // prompt (in place of the action chips) so the user acknowledges
  // the next-instance date before the advance commits. Mirrors the
  // agenda-row confirm cell. pendingAdvance carries the proposed
  // next Date; null = no prompt showing.
  const [pendingAdvance, setPendingAdvance] = useState(null);
  // Reset the confirm prompt whenever the popover gets a different
  // reminder doc (avoid carrying a stale prompt across rows).
  useEffect(() => { setPendingAdvance(null); }, [reminder.id]);
  // Seed the NL editor with the canonical form of the current
  // reminder. This is the bell-line minus the leading "🔔 " prefix
  // — the bell is implicit (it's a reminder), the user only edits
  // the part after.
  const initialNL = useMemo(() => canonicalNL(reminder), [reminder.id]);
  const [nlDraft, setNlDraft] = useState(initialNL);
  const [notesDraft, setNotesDraft] = useState(joinNotes(notes));
  // Stable string version of notes so the sync useEffect deps don't
  // re-fire every parent render — see the structured-version comment
  // for the full rationale.
  const notesContent = joinNotes(notes);
  const containerRef = useRef(null);
  const nlInputRef = useRef(null);

  // Viewport + visualViewport tracking — mobile keyboard avoidance.
  // Walter 2026-05-06 round 3: bottom-anchored sheet was getting
  // covered by the on-screen keyboard. Anchor to top of visible
  // viewport instead and bound height to visualViewport.height.
  const getViewport = () => {
    if (typeof window === 'undefined') {
      return { width: 1024, height: 800, offsetTop: 0 };
    }
    const v = window.visualViewport;
    return v
      ? { width: v.width, height: v.height, offsetTop: v.offsetTop }
      : { width: window.innerWidth, height: window.innerHeight, offsetTop: 0 };
  };
  const [vp, setVp] = useState(getViewport);
  useEffect(() => {
    const update = () => setVp(getViewport());
    window.addEventListener('resize', update);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', update);
      window.visualViewport.addEventListener('scroll', update);
    }
    return () => {
      window.removeEventListener('resize', update);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', update);
        window.visualViewport.removeEventListener('scroll', update);
      }
    };
  }, []);
  const isMobile = vp.width <= 640;

  // Re-seed when the reminder doc itself changes (another tab edited
  // it while this popover was open). We compare against the freshly-
  // computed canonical form rather than overwriting blindly — that
  // way a remote no-op doesn't wipe in-progress typing.
  useEffect(() => {
    const fresh = canonicalNL(reminder);
    setNlDraft(fresh);
  }, [reminder.id, reminder.text, reminder.parsedDate, reminder.recurrence]);

  useEffect(() => {
    setNotesDraft(notesContent);
  }, [reminder.id, notesContent]);

  // Esc dismisses; click-outside saves.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        commitDraft();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onMouseDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onMouseDown, true);
    };
  }, [nlDraft, notesDraft, reminder.id]);

  // Parse the current NL draft into a structured patch. Returns null
  // when the input is unparseable — caller decides whether to fall
  // back to existing values.
  const parsedDraft = useMemo(() => parseCanonicalNL(nlDraft), [nlDraft]);
  // Walter 2026-09-14 (reminder proposal, step 2): the date as it will be
  // SAVED, by the same rule as typing into a list (lib/resolveReminder). One
  // value feeds the payload, the edit patch AND the live preview, so what he
  // sees is what he gets. Before this, "tomorrow t3" saved chrono's clock
  // time — his 5:34pm.
  const resolvedDraftDate = useMemo(() => (parsedDraft ? resolveReminderDate({
    parsedDate: parsedDraft.parsedDate,
    hasCertainTime: parsedDraft.hasCertainTime,
    defaultTime: defaultReminderTime,
    existingDate: isCreateMode ? null : reminder?.parsedDate,
  }) : null), [parsedDraft, defaultReminderTime, isCreateMode, reminder?.parsedDate]);

  const commitDraft = () => {
    // Walter 2026-05-12: in create mode, we don't compute a diff —
    // the whole parsed draft becomes the new reminder doc. Empty
    // drafts (no parseable text) are dropped silently so an accidental
    // open+close of the create popover doesn't spam reminders.
    if (isCreateMode) {
      const trimmed = nlDraft.trim();
      if (!trimmed) return;
      const payload = parsedDraft
        ? {
            text: parsedDraft.text.trim() || trimmed,
            parsedDate: resolvedDraftDate || null,
            recurrence: normalizeRecurrence(parsedDraft.recurrence) || null,
          }
        : { text: trimmed, parsedDate: null, recurrence: null };
      const notesText = notesDraft && notesDraft.trim() ? notesDraft : '';
      onCreate(payload, notesText);
      return;
    }
    const patch = {};
    if (parsedDraft) {
      const trimmedText = parsedDraft.text.trim();
      if (trimmedText !== (reminder.text || '').trim()) patch.text = trimmedText;
      const existing = toDate(reminder.parsedDate);
      const next = resolvedDraftDate;
      if (next && (!existing || existing.getTime() !== next.getTime())) {
        patch.parsedDate = next;
      } else if (!next && existing) {
        patch.parsedDate = null;
      }
      const existingRec = normalizeRecurrence(reminder.recurrence);
      const nextRec = normalizeRecurrence(parsedDraft.recurrence);
      if (!recurrencesEqual(existingRec, nextRec)) {
        patch.recurrence = nextRec;
      }
    } else {
      // Unparseable input — preserve recurrence/date, just rewrite
      // text so the user's edit isn't lost entirely.
      const trimmed = nlDraft.trim();
      if (trimmed !== (reminder.text || '').trim()) patch.text = trimmed;
    }
    if (Object.keys(patch).length > 0) {
      onUpdate?.(reminder.id, patch);
    }
    if (onUpdateNotes && notesDraft !== notesContent) {
      onUpdateNotes(reminder, notesDraft);
    }
  };

  // Live preview of the bell-line as it WILL appear in the source
  // list. Re-derived on every keystroke from the parsed draft.
  const previewLine = useMemo(() => {
    if (parsedDraft) {
      return formatBellLine({
        ...reminder,
        text: parsedDraft.text,
        parsedDate: resolvedDraftDate,
        recurrence: parsedDraft.recurrence,
      });
    }
    // Unparseable: preview keeps current meta + new text. Better than
    // showing a blank or stale-meta preview.
    return formatBellLine({ ...reminder, text: nlDraft.trim() });
  }, [parsedDraft, nlDraft, reminder.status, resolvedDraftDate]);

  // Forward-looking "next instance" label. Recurring reminders advance
  // parsedDate after each fire, so the doc's parsedDate IS the next
  // scheduled occurrence. For one-offs the date IS the canonical form
  // already, so we suppress the annotation.
  const nextInstanceLabel = useMemo(() => {
    // Walter 2026-06-02: show the reminder's ACTUAL stored next fire, not
    // a value re-derived from the recurrence text. The stored parsedDate
    // is the source of truth the cron fires on — it already reflects
    // snoozes and post-fire advances. Previously this used the re-parsed
    // draft, which recomputed the next rule occurrence from scratch and
    // ignored snoozes: a snoozed "every Wed and Sun" showed "tomorrow"
    // here while the agenda (and the real schedule) said the snoozed day.
    // Only fall back to the parsed draft in create mode, where there's no
    // stored date yet.
    const useDraft = isCreateMode && parsedDraft;
    const rec = useDraft ? parsedDraft.recurrence : reminder.recurrence;
    const pd = useDraft ? resolvedDraftDate : toDate(reminder.parsedDate);
    if (!rec) return null;
    if (!pd) return null;
    return formatNextInstance(pd);
  }, [isCreateMode, parsedDraft, reminder.recurrence, reminder.parsedDate, resolvedDraftDate]);

  // Position — desktop side-popover anchored to the agenda row;
  // mobile top-anchored modal that stays in visualViewport.
  const popoverMargin = 12;
  const mobileMargin = 16;
  const popoverWidth = isMobile ? Math.max(280, vp.width - 16) : 540;
  const wide = !isMobile && anchorRect && anchorRect.left > popoverWidth + popoverMargin * 2;
  const mobileTop = vp.offsetTop + mobileMargin;
  const mobileMaxHeight = Math.max(200, vp.height - 2 * mobileMargin);
  const top = isMobile
    ? mobileTop
    : (anchorRect
      ? Math.max(12, Math.min(anchorRect.top, window.innerHeight - 340))
      : 80);
  const left = isMobile
    ? 8
    : (wide
      ? Math.max(popoverMargin, anchorRect.left - popoverWidth - popoverMargin)
      : Math.max(popoverMargin, (window.innerWidth - popoverWidth) / 2));

  const containerStyle = isMobile
    ? {
        position: 'fixed',
        top, left, width: popoverWidth,
        maxHeight: `${mobileMaxHeight}px`,
        background: 'var(--surface)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-picker-strong)',
        zIndex: 2400,
        display: 'flex', flexDirection: 'column',
        fontFamily: 'var(--mono)',
        color: 'var(--text)',
        overflow: 'hidden',
      }
    : {
        position: 'fixed',
        top, left, width: popoverWidth,
        // Walter 2026-05-25 (Bug #2 — round-2 tester report): when
        // editing the LAST reminder in the agenda, the popover anchored
        // near the bottom of the viewport, and the old maxHeight of
        // calc(100vh - 24px) let the popover extend BELOW the viewport
        // — so the sticky Save / Cancel footer was off-screen and
        // unreachable. Cap maxHeight to the space between `top` and
        // the viewport bottom (with a 12px margin) so the popover
        // always fits; the inner body scrolls if the content's tall.
        maxHeight: `${Math.max(200, window.innerHeight - top - 12)}px`,
        background: 'var(--surface)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-picker)',
        zIndex: 2400,
        display: 'flex', flexDirection: 'column',
        fontFamily: 'var(--mono)',
        color: 'var(--text)',
        overflow: 'hidden',
      };

  // Mobile-only blurred backdrop.
  const backdropStyle = isMobile ? {
    position: 'fixed',
    top: vp.offsetTop, left: 0,
    width: vp.width, height: vp.height,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    zIndex: 2399,
  } : null;

  return (
    <>
      {backdropStyle && (
        <div onClick={onClose} style={backdropStyle} aria-hidden="true" />
      )}
      <div
        ref={containerRef}
        onClick={e => e.stopPropagation()}
        style={containerStyle}
      >
        {/* Header. Walter 2026-05-14 (s4): bell glyph moved here
            (was sitting to the left of the textarea below, eating
            ~26px of horizontal space and indenting every other
            row). Now it reads as the popover's identity marker. */}
        <div style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
        }}>
          <span style={{
            color: 'var(--accent)',
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            lineHeight: 1,
          }} aria-hidden="true">🔔</span>
          <span>edit reminder</span>
          <span style={{ flex: 1 }} />
          {onJump && sourceList && (
            <button
              onClick={() => { commitDraft(); onJump(); }}
              title="open the source list (closes this editor)"
              style={inlineLinkBtnStyle}
            >
              open source list →
            </button>
          )}
          <button
            onClick={onClose}
            title="close (Esc)"
            style={iconBtnStyle}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 2L10 10 M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body — NL editor on the left, source preview on the right. */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flex: 1, minHeight: 0,
          overflowY: isMobile ? 'auto' : 'hidden',
        }}>
          {/* NL editor */}
          <div style={{
            flex: isMobile ? '0 0 auto' : '1 1 280px',
            minWidth: isMobile ? 0 : 240,
            padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: 10,
            fontSize: 'calc(12px * var(--ui-scale, 1))',
            borderRight: isMobile ? 'none' : '1px solid var(--border)',
            borderBottom: isMobile ? '1px solid var(--border)' : 'none',
          }}>
            {/* Single NL input. Walter 2026-05-14 (s4): bell glyph
                moved to the header above so this row uses the full
                width. Walter 2026-05-21 (s1): wrapped in a labelled
                Field ("date and topic") — the box was unlabelled and
                the placeholder alone didn't make its purpose obvious,
                especially next to the labelled "notes" box below. */}
            <Field label="date and topic">
              <textarea
                ref={nlInputRef}
                value={nlDraft}
                onChange={e => setNlDraft(e.target.value)}
                autoFocus
                rows={Math.max(2, Math.min(4, nlDraft.split('\n').length + 1))}
                placeholder='e.g. "every Tue at 8am: book squash" or "tomorrow 9am: dentist"'
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: 56,
                  lineHeight: 1.4,
                }}
              />
            </Field>
            {/* Forward-looking next-instance annotation — only shown
                for recurring reminders where parsedDate is in the
                future. Non-editable per Walter's spec: it's a
                derived view of the rule, not part of the input.
                Walter 2026-05-13: pulled closer to the textarea and
                left-aligned with it (22px = the 🔔 + 8px gap above)
                so it reads as an annotation of the rule, not a
                separate field. */}
            {nextInstanceLabel && (
              <div style={{
                marginTop: -4,
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                color: 'var(--text-dim)',
              }}>
                next instance: <span style={{ color: 'var(--accent)' }}>{nextInstanceLabel}</span>
              </div>
            )}
            {/* Row-action set — Walter 2026-05-13 (s1). Same icons
                as the agenda row, just spelled out here so the
                popover is a self-contained edit surface. Renders
                only when the caller wired the corresponding
                handlers (AgendaPanel does; if other callers
                re-use this popover without wiring them, the row
                hides). */}
            {/* Walter 2026-05-14 (round 13): when the user clicks
                "did it" on a recurring reminder, swap the action
                chips for an explicit confirm prompt showing the
                proposed next instance. Same model as the agenda
                row's confirm cell. Confirm → run onMarkDone (which
                advances + closes). Cancel → restore the chip row. */}
            {pendingAdvance ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px',
                marginTop: 2,
                background: 'var(--accent-wash, rgba(196,81,30,0.10))',
                border: '1px solid var(--accent-dim)',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
              }}>
                <div style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {reminder.parsedDate
                      ? `${formatNextInstance(reminder.parsedDate)} done`
                      : 'this one done'}
                  </div>
                  <div style={{ color: 'var(--text-dim)' }}>
                    next instance: <span style={{ color: 'var(--text)', fontWeight: 600 }}>{formatNextInstance(pendingAdvance)}</span>
                  </div>
                </div>
                <button
                  onClick={() => setPendingAdvance(null)}
                  style={{
                    background: 'transparent',
                    color: 'var(--text-dim)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    padding: '5px 10px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'inherit',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >cancel</button>
                <button
                  onClick={() => { setPendingAdvance(null); onMarkDone?.(); }}
                  style={{
                    background: 'var(--accent)',
                    color: 'var(--bg)',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    padding: '5px 12px',
                    fontFamily: 'var(--mono)',
                    fontSize: 'inherit',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >confirm</button>
              </div>
            ) : (onMarkDone || onSnooze || onRemoveDate || onDeactivate || onDelete || onStopReminding) && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 2,
              }}>
                {onMarkDone && (
                  <RowAction
                    onClick={() => {
                      // Recurring: stage the confirm prompt instead of
                      // committing immediately. One-off (or rule shape
                      // we can't advance, like bySetPos): act now.
                      const rec = reminder.recurrence;
                      const prevDate = reminder.parsedDate?.toDate?.() || reminder.parsedDate || null;
                      const next = rec ? computeNextOccurrence(prevDate || new Date(), rec) : null;
                      if (rec && next instanceof Date && !isNaN(next.getTime())) {
                        setPendingAdvance(next);
                      } else {
                        onMarkDone();
                      }
                    }}
                    title={reminder.recurrence
                      ? 'mark this occurrence done — you\'ll confirm the next-instance date'
                      : 'mark done'}
                    icon="check"
                  >
                    {reminder.recurrence ? 'did it' : 'done'}
                  </RowAction>
                )}
                {onSnooze && (
                  <RowAction onClick={onSnooze} title="snooze 1 day">snooze 1d</RowAction>
                )}
                {onRemoveDate && reminder.parsedDate && (
                  <RowAction onClick={onRemoveDate} title="remove the date — becomes an undated reminder">drop date</RowAction>
                )}
                {/* Walter 2026-05-13 (round 9): "stop reminding" only
                    on recurring reminders. Kills the rule entirely so
                    no future instance fires. Distinct from "did it"
                    which advances to the next occurrence. */}
                {onStopReminding && (
                  <RowAction
                    onClick={onStopReminding}
                    title="turn off this recurring reminder — no further instances will fire"
                  >
                    stop reminding
                  </RowAction>
                )}
                {onDeactivate && (
                  <RowAction onClick={onDeactivate} title="strip the bell prefix from the source line — the line stays, the reminder doc goes">deactivate</RowAction>
                )}
                {onDelete && (
                  <RowAction onClick={onDelete} tone="danger" title="delete the source line entirely">delete</RowAction>
                )}
              </div>
            )}
            {/* Notes editor */}
            <Field label="notes">
              <textarea
                value={notesDraft}
                onChange={e => setNotesDraft(e.target.value)}
                placeholder="any notes…"
                rows={Math.max(3, Math.min(10, notesDraft.split('\n').length + 1))}
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: 60,
                  fontFamily: 'var(--mono)',
                  lineHeight: 1.4,
                }}
              />
            </Field>
          </div>

          {/* Read-only source preview. */}
          <div style={{
            flex: isMobile ? '0 0 auto' : '1 1 220px',
            minWidth: isMobile ? 0 : 200,
            padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: 6,
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            background: 'var(--surface2)',
            minHeight: 0,
            overflow: isMobile ? 'visible' : 'hidden',
          }}>
            <div style={{
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dimmer)',
              textTransform: 'uppercase', letterSpacing: '0.5px',
              marginBottom: 2,
            }}>
              source preview
              {sourceList?.title ? (
                <span style={{ textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>
                  · {sourceList.title}
                </span>
              ) : null}
            </div>
            <div style={{
              fontFamily: 'var(--mono)',
              color: 'var(--text)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              overflowY: 'auto',
              flex: 1, minHeight: 60,
              paddingRight: 4,
            }}>
              <div style={{
                background: 'var(--accent-wash, rgba(196,81,30,0.15))',
                borderLeft: '2px solid var(--accent)',
                paddingLeft: 6, marginLeft: -8,
                color: 'var(--text)',
                fontWeight: 500,
              }}>
                {previewLine}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky save/cancel footer. */}
        <div style={{
          flexShrink: 0,
          padding: '10px 14px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={onClose} style={ghostBtnStyle}>cancel</button>
          <button onClick={() => { commitDraft(); onClose(); }} style={primaryBtnStyle}>save</button>
        </div>
      </div>
    </>
  );
}

// Compact button used inside the popover's row-action set. Tone
// 'danger' renders in the danger color (delete). Default is a quiet
// outlined chip. `icon="check"` prepends the same faint checkmark
// glyph the agenda row's mark-done checkbox uses — Walter
// 2026-05-14 (#5).
function RowAction({ children, onClick, title, tone, icon }) {
  const dangerous = tone === 'danger';
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      onMouseDown={e => e.stopPropagation()}
      style={{
        background: 'transparent',
        border: '1px solid ' + (dangerous ? 'var(--danger, #d04a4a)' : 'var(--border)'),
        borderRadius: 'var(--radius)',
        color: dangerous ? 'var(--danger, #d04a4a)' : 'var(--text-dim)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        padding: '3px 8px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.color = dangerous ? 'var(--danger, #d04a4a)' : 'var(--accent)';
        if (!dangerous) e.currentTarget.style.borderColor = 'var(--accent-dim)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.color = dangerous ? 'var(--danger, #d04a4a)' : 'var(--text-dim)';
        if (!dangerous) e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      {icon === 'check' && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path
            d="M2 5l2 2 4-4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.7}
          />
        </svg>
      )}
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        color: 'var(--text-dimmer)',
        textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>{label}</span>
      {children}
    </label>
  );
}

// Compose the canonical NL form for the editor — bell-line minus the
// leading "🔔 " prefix. Empty when the reminder has nothing meta to
// show (text-only "🔔 something" survives as just "something").
function canonicalNL(reminder) {
  if (!reminder) return '';
  const line = formatBellLine(reminder);
  // formatBellLine output starts with "🔔 " (open) or "[DONE] " (done).
  // Legacy "🔕 " and "~~🔔 ...~~" strikethrough are also stripped for
  // backwards compatibility. Trailing single space is consumed.
  return line
    .replace(/^\[DONE\]\s?/, '')
    .replace(/^~~🔔\s?/, '')
    .replace(/^🔕\s?/, '')
    .replace(/^🔔\s?/, '');
}

// Parse the canonical NL back into a structured patch shape. Returns
// { text, parsedDate, recurrence } on success or null on failure.
// Preprocesses the user-friendly ":" separator (between meta and
// task) so parseReminderLine sees a clean "every Tue 8am task"-style
// input regardless of whether the user typed the colon.
function parseCanonicalNL(input) {
  const raw = (input || '').trim();
  if (!raw) return { text: '', parsedDate: null, recurrence: null };
  // Strip the FIRST ": " (colon followed by space) — that's our display
  // separator between metadata ("every Tue at 8am") and task text.
  // We match colon+space specifically so a URL in the body
  // ("call mom https://example.com") keeps its scheme colon intact.
  // Only the leading occurrence is stripped; subsequent ": " inside
  // the task text passes through unchanged.
  const sepIdx = raw.indexOf(': ');
  const cleaned = sepIdx >= 0
    ? (raw.slice(0, sepIdx) + ' ' + raw.slice(sepIdx + 2)).replace(/\s+/g, ' ').trim()
    : raw;
  const parsed = parseReminderLine(cleaned);
  if (!parsed.isReminder) {
    return { text: cleaned, parsedDate: null, recurrence: null };
  }
  return {
    text: parsed.restText || '',
    parsedDate: parsed.parsedDate || null,
    recurrence: parsed.recurrence || null,
    // Walter 2026-09-14: keep the parser's "was a time typed?". Dropping
    // it here is why the popover saved chrono's clock time.
    hasCertainTime: !!parsed.hasCertainTime,
  };
}

// Forward-looking label for the "next instance" annotation. Uses
// relative wording ("today", "tomorrow") when within 48h, otherwise
// "Tue 5/12". Always pairs with a time-of-day suffix.
function formatNextInstance(date) {
  const d = toDate(date);
  if (!d) return null;
  const now = new Date();
  const time = formatTimeShort(d);
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) return `today at ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.getFullYear() === tomorrow.getFullYear()
    && d.getMonth() === tomorrow.getMonth()
    && d.getDate() === tomorrow.getDate();
  if (isTomorrow) return `tomorrow at ${time}`;
  const dow = d.toLocaleDateString(undefined, { weekday: 'short' });
  // Walter 2026-05-12: year suffix between date and time when the
  // instance is in a non-current year, matching the source-line format.
  const md = formatMonthDay(d);
  const datePart = d.getFullYear() !== now.getFullYear()
    ? `${md} '${String(d.getFullYear()).slice(-2)}`
    : md;
  return `${dow} ${datePart} at ${time}`;
}

// Notes helpers — same shape as the structured version.
function joinNotes(notes) {
  if (!Array.isArray(notes) || notes.length === 0) return '';
  return notes.map(n => n.replace(/^[\s\-*·.+>]\s*/, '')).join('\n');
}

function recurrencesEqual(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.freq !== b.freq) return false;
  if ((a.interval || 1) !== (b.interval || 1)) return false;
  const aDays = (a.byDay || []).join(',');
  const bDays = (b.byDay || []).join(',');
  if (aDays !== bDays) return false;
  if ((a.byMonthDay || null) !== (b.byMonthDay || null)) return false;
  if ((a.bySetPos || null) !== (b.bySetPos || null)) return false;
  if ((a.count || null) !== (b.count || null)) return false;
  if ((a.until || null) !== (b.until || null)) return false;
  return true;
}

function toDate(d) {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d?.toDate === 'function') return d.toDate();
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
}

const inputStyle = {
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(12px * var(--ui-scale, 1))',
  padding: '6px 8px',
  outline: 'none',
};

const inlineLinkBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(10px * var(--ui-scale, 1))',
  padding: 0,
  textDecoration: 'underline',
};

const iconBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-dim)',
  cursor: 'pointer',
  padding: 4,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const ghostBtnStyle = {
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-dim)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(11px * var(--ui-scale, 1))',
  padding: '5px 12px',
  cursor: 'pointer',
};

const primaryBtnStyle = {
  background: 'var(--accent)',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius)',
  color: 'var(--bg)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(11px * var(--ui-scale, 1))',
  padding: '5px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
